/**
 * Force Lock Confirmation Dialog
 * 
 * Confirms with the user before forcibly taking over a lock from another user.
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

export interface ForceLockDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Name of the user who currently has the lock */
  lockedByName: string;
  /** Callback when user confirms the force takeover */
  onConfirm: () => void;
  /** Whether the force takeover is in progress */
  isLoading?: boolean;
}

export function ForceLockDialog({
  open,
  onOpenChange,
  lockedByName,
  onConfirm,
  isLoading = false,
}: ForceLockDialogProps) {
  const { t } = usePlatformTranslation();

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('lock.forceTakeoverTitle', 'Take Over Edit Lock?')}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              {t('lock.forceTakeoverDescription', 
                '{{name}} is currently editing this item.', 
                { name: lockedByName }
              )}
            </p>
            <div className="text-sm">
              <p className="font-medium mb-1">
                {t('lock.forceTakeoverWarningTitle', 'Taking over will:')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>
                  {t('lock.forceTakeoverWarning1', 'Remove their edit access immediately')}
                </li>
                <li>
                  {t('lock.forceTakeoverWarning2', 'They may lose unsaved work')}
                </li>
                <li>
                  {t('lock.forceTakeoverWarning3', 'They will be notified')}
                </li>
              </ul>
            </div>
            <p className="text-sm font-medium">
              {t('lock.forceTakeoverConfirm', 'Are you sure you want to proceed?')}
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {t('common.cancel', 'Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading 
              ? t('lock.takingOver', 'Taking Over...') 
              : t('lock.takeOverLock', 'Take Over Lock')
            }
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ForceLockDialog;
